import { Section } from "@affinity/components/Section";
import { AddressType } from "@affinity/constants/SharedTypes";
import addAddress from "@affinity/Handlers/AddAddress";
import PlaceOrderGetPricing from "@affinity/Handlers/PlaceOrderGetPricing";
import { BindPrePopulate, PlaceOrderQueryModel, PopulateDelegate } from "@affinity/Store/PrePopulation";
import { CustomState } from "@affinity/Store/Reducers";
import { AffinityProduct } from "@affinity/Store/StoreTypes";
import AddressesSelector from "@affinity/Widgets/PlaceOrder/AddressesSelector";
import createShipTo from "@insite/client-framework/Store/Pages/Addresses/Handlers/CreateShipTo";
import loadBillTo from "@insite/client-framework/Store/Pages/BudgetManagement/Handlers/LoadBillTo";
import updateBillTo from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/UpdateBillTo";
import { ShipToModel } from "@insite/client-framework/Types/ApiModels";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import * as React from "react";
import { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: CustomState) => ({
    // Map required state elements to component properties
    currentUserId: state.context.session.userProfileId,
    currentBillToId: state.context.session.billToId,
    quantity: state.affinity_place_order.quantity!,
    selectedProduct: state.affinity_place_order.selectedProduct === AffinityProduct.Regular
        ? state.affinity_place_order.productRegular
        : state.affinity_place_order.productPremium,
    populateFunction: BindPrePopulate(state),
    addresses: state.affinity_place_order.existingAddresses,
});

const mapDispatchToProps = {
    // Map loader and setter methods to component properties
    PlaceOrderGetPricing,
    createShipTo,
    updateBillTo,
    loadBillTo,
    addAddress,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export const Step3SelectAddress: React.FC<Props> = ({
    PlaceOrderGetPricing,
    currentUserId,
    currentBillToId,
    quantity,
    selectedProduct,
    addAddress,
    history,
    populateFunction,
    addresses,
}) => {

    const popSetAddress: PopulateDelegate = function (data) {
        // Return if no data
        if (!data.postcode) {
            return undefined;
        }

        // Trim data for URL update
        const updatedQuery: PlaceOrderQueryModel = {
            ...data,
            product: undefined,
            quantity: undefined,
            postcode: undefined,
        }

        // Check known values
        if (!addresses) {
            return undefined;
        }
        const findPredicate = (value: ShipToModel) => {
            return value.postalCode.toLocaleLowerCase().trim() === data.postcode?.toLocaleLowerCase()?.trim();
        };
        if (addresses.shipTos?.filter(findPredicate).length !== 1) {
            // Return if no or multiple results found.
            return undefined;
        }

        const addressExists = addresses.shipTos?.find(findPredicate);
        if (!addressExists) {
            // Return if ship to model instance not found.
            return undefined;
        }

        // Apply found address
        PlaceOrderGetPricing({ 
            selectedAddress: addressExists, 
            selectedQuantity: quantity, 
            selectedProduct
        });

        return updatedQuery;
    };

    const onAddAddress = (value: AddressType) => {
        addAddress({
            currentUserId: currentUserId!,
            billToId: currentBillToId,
            phone: '',
            ...value
        });
    };

    const onAddressSelect = (address: ShipToModel) => {
        PlaceOrderGetPricing({ 
            selectedAddress: address, 
            selectedQuantity: quantity, 
            selectedProduct
        });
    };

    let popResult: JSX.Element | undefined = undefined;
    useEffect(() => {
        if (addresses && addresses.shipTos) {
            popResult = populateFunction(popSetAddress, history);
        }
    }, [addresses])

    if (popResult) {
        return popResult;
    }

    return (
        <Section title="3. Delivery address" shouldFocus={true}>
            <AddressesSelector onAddAddress={onAddAddress} onAddressSelect={onAddressSelect} />
        </Section>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(Step3SelectAddress));
